import { useEffect, useRef } from 'react';
import { gpsApi } from '../../api/gps.api';
import { useAuth } from '../../store/AuthContext';
import { isEmployee } from '../../utils/roleHelpers';

// Sends a GPS ping every ~5 minutes while an employee has the dashboard
// open (tab visible), so managers can see where employees have been in the
// daily report. Tracking stops the moment the tab is closed/hidden or the
// employee logs out - nothing runs in the background.
const PING_INTERVAL_MS = 5 * 60 * 1000;

export default function GpsTracker() {
  const { user } = useAuth();
  const enabled = isEmployee(user?.type);
  const deniedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !('geolocation' in navigator)) return undefined;

    const sendPing = () => {
      if (deniedRef.current || document.visibilityState !== 'visible') return;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          gpsApi.updateLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy_meters: position.coords.accuracy ? Math.round(position.coords.accuracy) : null,
            is_active: true,
          }).catch(() => {});
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            deniedRef.current = true;
          }
        },
        { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
      );
    };

    sendPing();
    const intervalId = setInterval(sendPing, PING_INTERVAL_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendPing();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);

  return null;
}
